import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { parseDecimal, toInputComma } from "@/lib/format";
import { ArrowLeft, Upload, Save, Loader2, FileDown, FileSpreadsheet } from "lucide-react";
import DirectoryPicker from "@/components/DirectoryPicker";
import PorijekloItems from "@/components/porijeklo/PorijekloItems";
import PorijekloSummary from "@/components/porijeklo/PorijekloSummary";
import { downloadExport } from "@/lib/porijeklo";

const CUSTOMER_FIELDS = [
  { key: "customer_type", label: "Tip", type: "select", options: ["Firma", "Fizičko lice"] },
  { key: "company_name", label: "Naziv firme / ime", required: true },
  { key: "country", label: "Država", type: "select", options: ["Bosna i Hercegovina", "Slovenija", "Austrija", "Njemačka", "Ostalo"] },
];

export default function PorijekloEditor({ initial, onClose }) {
  const [doc, setDoc] = useState({ origin_country: "TR", items: [], ...initial });
  const [fcaRaw, setFcaRaw] = useState(initial.fca_eur ? toInputComma(initial.fca_eur) : "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (patch) => setDoc((d) => ({ ...d, ...patch }));

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/porijeklo-parse", fd);
      const { customer_name, ...rest } = r.data;
      set({ ...rest, customer_name: doc.customer_name || customer_name });
      toast.success(`Učitano ${r.data.items.length} stavki, ${r.data.items.filter((i) => i.has_origin).length} sa porijeklom`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri čitanju PDF-a");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!doc.customer_name) { toast.error("Odaberite kupca"); return; }
    if (!doc.items.length) { toast.error("Uploadujte izdatnicu materijala"); return; }
    const payload = { ...doc, fca_eur: parseDecimal(fcaRaw), items: doc.items.map(({ _raw, ...i }) => i) };
    try {
      const r = doc.id ? await api.put(`/porijeklo/${doc.id}`, payload) : await api.post("/porijeklo", payload);
      setDoc((d) => ({ ...d, id: r.data.id }));
      toast.success("Izjava spremljena — možete preuzeti PDF / Excel");
    } catch (e) {
      toast.error("Greška pri spremanju");
    }
  };

  return (
    <div className="space-y-6" data-testid="porijeklo-editor">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" onClick={onClose} data-testid="porijeklo-back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Nazad</Button>
        <h1 className="text-2xl sm:text-3xl font-extrabold font-heading tracking-tight">{doc.id ? "Uredi izjavu o porijeklu" : "Nova izjava o porijeklu"}</h1>
      </div>

      <div className="rounded-xl border bg-card p-4 grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-xs font-semibold text-muted-foreground">Izdatnica materijala (PDF)</Label>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="porijeklo-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="porijeklo-upload-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Uploaduj izdatnicu
            </Button>
            {doc.doc_number && <span className="text-sm text-muted-foreground" data-testid="porijeklo-doc-info">{doc.doc_number} · {doc.product}</span>}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-muted-foreground">Kupac</Label>
          <DirectoryPicker
            endpoint="customers"
            placeholder={doc.customer_name || "Odaberi kupca iz imenika..."}
            groupBy="country"
            getLabel={(c) => c.company_name || c.customer_name}
            createFields={CUSTOMER_FIELDS}
            onPick={(c) => set({ customer_name: c.company_name || c.customer_name })}
            testid="porijeklo-customer-picker"
          />
          {doc.customer_name && <div className="text-xs text-muted-foreground" data-testid="porijeklo-customer-name">Odabrano: <b>{doc.customer_name}</b></div>}
          <Label className="text-xs font-semibold text-muted-foreground pt-2 block">Napomena</Label>
          <Input value={doc.note || ""} placeholder="npr. objekat, broj narudžbe..." onChange={(e) => set({ note: e.target.value })} data-testid="porijeklo-note-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-muted-foreground">Cijena proizvoda FCA fabrika (EUR)</Label>
            <Input inputMode="decimal" placeholder="npr. 1585,00" value={fcaRaw} onChange={(e) => { setFcaRaw(e.target.value.replace(/[^0-9.,]/g, "")); set({ fca_eur: parseDecimal(e.target.value) }); }} data-testid="porijeklo-fca-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-muted-foreground">Porijeklo (za crvene stavke)</Label>
            <Input value={doc.origin_country || ""} onChange={(e) => set({ origin_country: e.target.value.toUpperCase() })} data-testid="porijeklo-origin-input" />
          </div>
        </div>
      </div>

      {doc.items.length > 0 && (
        <>
          <PorijekloSummary doc={doc} />
          <PorijekloItems items={doc.items} originDefault={doc.origin_country} onChange={(items) => set({ items })} />
        </>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={save} data-testid="porijeklo-save-btn"><Save className="h-4 w-4 mr-1.5" /> Spremi izjavu</Button>
        {doc.id && (
          <>
            <Button variant="outline" onClick={() => downloadExport(doc.id, "pdf")} data-testid="porijeklo-editor-pdf"><FileDown className="h-4 w-4 mr-1.5" /> Preuzmi PDF</Button>
            <Button variant="outline" onClick={() => downloadExport(doc.id, "xlsx")} data-testid="porijeklo-editor-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Preuzmi Excel</Button>
          </>
        )}
      </div>
    </div>
  );
}
